/**
 * Institutions an income certificate is commonly presented to.
 *
 * A portal-side constant rather than a DB table on purpose: this is display
 * copy that is never joined against anything, and a table would demand a
 * migration, an admin CRUD screen and a seeding story for no gain. That
 * trade flips the day an institution gains its own certificate template or
 * routing rule — at that point this list wants to become a real table.
 *
 * The list going stale is expected and harmless: `other` is always offered
 * and its free text is always accepted, so a renamed or acquired institution
 * costs a slightly wrong label, never a blocked user.
 *
 * What actually gets persisted is the LABEL, not the value: the wizard resolves
 * the selection to its display name before sending it, so `incomeCertificateDetails`
 * on an existing request holds "BROU — Banco República", not "brou". That keeps
 * every read path — both summaries, and any ops export — renderable with no
 * lookup table, at the cost of making labels the stored vocabulary. So renaming
 * a label splits aggregation: old requests keep the old string while new ones
 * get the new one. Treat label edits as data changes, not copy changes. The
 * `value` is only a local list key and is safe to change.
 */
import type { IncomePresentingEntityPreset } from "@/hooks/useIncomeCertificateWizardFormState";

export const PRESENTING_INSTITUTION_OTHER = "other" as const;

export interface PresentingInstitution {
  value: string;
  label: string;
}

export const PRESENTING_INSTITUTIONS: readonly PresentingInstitution[] = [
  // Banks
  { value: "brou", label: "BROU — Banco República" },
  { value: "santander", label: "Santander" },
  { value: "itau", label: "Itaú" },
  { value: "scotiabank", label: "Scotiabank" },
  { value: "bbva", label: "BBVA" },
  { value: "hsbc", label: "HSBC" },
  { value: "banco_heritage", label: "Banco Heritage" },
  // Financieras / credit
  { value: "oca", label: "OCA" },
  { value: "creditel", label: "Creditel" },
  { value: "pronto", label: "Pronto!" },
  { value: "republica_microfinanzas", label: "República Microfinanzas" },
];

/**
 * Categories for which naming a specific institution is meaningful. Every
 * other category already captures the specific body in the existing
 * `presentingEntityOther` free text, so asking twice would double-ask.
 */
// Typed to the preset union, not string[]: a renamed preset must fail to
// compile here rather than silently making the gate always false and
// making the institution question disappear.
export const PRESENTING_ENTITY_KEYS_WITH_INSTITUTION: readonly IncomePresentingEntityPreset[] = [
  "bank",
  "lender",
];
